import { createI18n } from 'vue-i18n'
import fr from './messages/fr-FR'
import en from './messages/en-GB'
import { datetimeFormats, numberFormats } from './formats'

/**
 * Localisation.
 *
 * ## Why French is the default rather than a fallback
 *
 * The agency this is built for works in French, and every other component in their estate ships
 * `fr-FR`. English was never the neutral choice here — it was the accident of the language the
 * first strings happened to be typed in. So `fr-FR` is the **default and the fallback**: a key
 * with no French translation renders the French of `fr-FR`'s own catalogue if it exists, and only
 * then falls through. `en-GB` is kept as a real second locale, not deleted, because the API,
 * the protocol and the operator documentation are all in English and somebody reading a support
 * thread needs to be able to switch.
 *
 * ## The rule that matters
 *
 * **Nothing user-visible is a literal in a component.** A string in a template is a string that
 * exists in exactly one language, and the next person to add a screen will copy whatever the
 * neighbouring screen did. Keys are grouped by the surface they appear on, so a translator can
 * work a screen at a time rather than hunting a flat list.
 *
 * ## Formatting is part of the locale, not part of the component
 *
 * Dates, times, numbers and percentages are formatted through this instance's
 * {@link datetimeFormats} and {@link numberFormats}, never through a hard-coded
 * `Intl.DateTimeFormat(undefined, …)`. `undefined` means "the browser's locale", which is not the
 * same as "the locale the user chose in this application" — a French operator on an
 * English-locale laptop was reading `Aug 14, 2:03 PM` in an otherwise French interface.
 */

export const SUPPORTED_LOCALES = ['fr-FR', 'en-GB'] as const

export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: AppLocale = 'fr-FR'

export const LOCALE_LABELS: Record<AppLocale, string> = {
  'fr-FR': 'Français',
  'en-GB': 'English',
}

const LOCALE_KEY = 'oui-observe.locale'

const isSupported = (value: unknown): value is AppLocale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)

/**
 * The locale to start in.
 *
 * An explicit choice wins. Absent one, a browser that asks for French in any regional flavour —
 * `fr`, `fr-BE`, `fr-CA` — gets `fr-FR`; everything else gets French too, because that is the
 * default rather than a guess. The browser check exists only so that an English-speaking
 * colleague's first paint is not a screen they have to go and change.
 */
const readStoredLocale = (): AppLocale => {
  try {
    const stored = globalThis.localStorage?.getItem(LOCALE_KEY)
    if (isSupported(stored)) return stored
  } catch {
    /* storage disabled — fall through to the language check */
  }

  const languages = globalThis.navigator?.languages ?? []
  for (const language of languages) {
    if (language.toLowerCase().startsWith('fr')) return 'fr-FR'
    if (language.toLowerCase().startsWith('en')) return 'en-GB'
  }

  return DEFAULT_LOCALE
}

export const i18n = createI18n({
  // The Composition API mode. `legacy: true` installs a global mixin and a second, different
  // `$t` on every component instance, which is how a codebase ends up with two ways to translate.
  legacy: false,
  globalInjection: true,
  locale: readStoredLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { 'fr-FR': fr, 'en-GB': en },
  datetimeFormats,
  numberFormats,
  // A missing key is a bug, and in development it should be loud. In production it must never
  // render a raw key path to a customer, so the fallback catalogue answers instead.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
})

/**
 * Translate from outside a component.
 *
 * Stores raise messages too, and a store is not a component — there is no `useI18n()` to call in
 * one that was instantiated before the app mounted. This binds to the same global instance, so a
 * locale switch moves store-raised toasts as well as templates.
 */
export const t: (typeof i18n)['global']['t'] = i18n.global.t

export const currentLocale = (): AppLocale => i18n.global.locale.value as AppLocale

/**
 * Switch locale, persist it, and tell the document.
 *
 * `<html lang>` is not decoration: it is what a screen reader uses to choose a voice, and what the
 * browser uses to hyphenate. An interface that renders French inside `lang="en"` is read aloud in
 * an English accent, which is worse than untranslated.
 */
export const setLocale = (locale: AppLocale): void => {
  i18n.global.locale.value = locale

  try {
    globalThis.localStorage?.setItem(LOCALE_KEY, locale)
  } catch {
    /* the switch still applies for this session */
  }

  const root = globalThis.document?.documentElement
  if (root) root.setAttribute('lang', locale)
}

/** Apply the stored locale to the document before the first paint. */
export const applyLocale = (): void => setLocale(currentLocale())
